import { addDoc, collection, doc, getDoc, getDocs, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase";

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
};

const toTimestamp = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return Timestamp.fromDate(value.toDate());
  if (typeof value?.seconds === "number") return new Timestamp(value.seconds, value.nanoseconds || 0);
  if (value instanceof Date) return Timestamp.fromDate(value);
  if (typeof value === "number") return Timestamp.fromDate(new Date(value));
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return Timestamp.fromDate(parsed);
  }
  return null;
};

const normalizeMember = (docSnap) => {
  const data = docSnap?.data?.() || {};
  return {
    ...data,
    id: docSnap.id,
    startDate: toMillis(data?.startDate),
    endDate: toMillis(data?.endDate),
    createdAt: toMillis(data?.createdAt),
    updatedAt: toMillis(data?.updatedAt),
    status: Boolean(data?.status),
    state: data?.state === undefined ? true : Boolean(data?.state),
  };
};

const buildServiceError = (error, fallbackMessage) => ({
  message: error?.message || fallbackMessage || "Beklenmeyen bir hata oluştu.",
  code: error?.code || "UNKNOWN_ERROR",
});

const listTeamMembers = async () => {
  try {
    const snap = await getDocs(collection(db, "beetinqteam"));
    const members = snap.docs.map((docSnap) => normalizeMember(docSnap));
    members.sort((a, b) => {
      const ao = Number(a?.order ?? 0);
      const bo = Number(b?.order ?? 0);
      if (ao !== bo) return ao - bo;
      return String(a?.name || "").localeCompare(String(b?.name || ""), "tr");
    });
    return members;
  } catch (error) {
    throw buildServiceError(error, "Beetinq ekibi yüklenemedi.");
  }
};

const addTeamMember = async ({ name, position, photo, order, startDate, endDate, userId }) => {
  try {
    const payload = {
      name: (name || "").trim(),
      position: (position || "").trim(),
      photo: (photo || "").trim(),
      order: Number.isFinite(Number(order)) ? Number(order) : 0,
      userId: (userId || "").trim(),
      state: true,
      status: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const parsedStart = toTimestamp(startDate);
    const parsedEnd = toTimestamp(endDate);
    payload.startDate = parsedStart || null;
    payload.endDate = parsedEnd || null;

    const ref = await addDoc(collection(db, "beetinqteam"), payload);
    const snap = await getDoc(doc(db, "beetinqteam", ref.id));
    return normalizeMember(snap);
  } catch (error) {
    throw buildServiceError(error, "Ekip üyesi eklenemedi.");
  }
};

const setTeamMemberStatus = async ({ id, status }) => {
  try {
    const ref = doc(db, "beetinqteam", id);
    const nextStatus = Boolean(status);
    await updateDoc(ref, {
      status: nextStatus,
      state: nextStatus,
      updatedAt: Timestamp.now(),
    });
    const snap = await getDoc(ref);
    return normalizeMember(snap);
  } catch (error) {
    throw buildServiceError(error, "Ekip üyesi güncellenemedi.");
  }
};

const updateTeamMember = async ({ id, name, position, photo, order, startDate, endDate }) => {
  try {
    const ref = doc(db, "beetinqteam", id);
    const payload = {
      name: (name || "").trim(),
      position: (position || "").trim(),
      photo: (photo || "").trim(),
      order: Number.isFinite(Number(order)) ? Number(order) : 0,
      updatedAt: Timestamp.now(),
    };

    const parsedStart = toTimestamp(startDate);
    const parsedEnd = toTimestamp(endDate);
    if (parsedStart) payload.startDate = parsedStart;
    if (parsedEnd) payload.endDate = parsedEnd;

    await updateDoc(ref, payload);
    const snap = await getDoc(ref);
    return normalizeMember(snap);
  } catch (error) {
    throw buildServiceError(error, "Ekip üyesi düzenlenemedi.");
  }
};

const beetinqTeamService = {
  listTeamMembers,
  addTeamMember,
  setTeamMemberStatus,
  updateTeamMember,
};

export default beetinqTeamService;
